import _ from 'lodash';

import PartialWrapper from './partials/PartialWrapper';
import Shapes, { LAYERS as SHAPE_LAYERS } from './partials/Shapes';
import { getBasemapLayerStyles } from '../basemapStyle';

import RenderByHelper from 'common/visualizations/helpers/RenderByHelper';
import SoqlHelpers from 'common/visualizations/helpers/SoqlHelpers';
import { getTileUrl } from 'common/visualizations/helpers/MapHelper';

const COLOR_BY_CATEGORY_ALIAS = '__color_by_category__';
const COUNT_ALIAS = '__count__';
const ROWID_ALIAS = '__row_id__';

// Prepares renderOptions and renders partials.
// Refer: common/visualizations/views/map/README.md
export default class VifShapeOverlay {
  constructor(map, mouseInteractionHandler) {
    const seriesId = _.uniqueId();

    this._shapes = new PartialWrapper(map, new Shapes(map, seriesId));
    this._mouseInteractionHandler = mouseInteractionHandler;
  }

  // Renders the partials required for this overlay and sets up required mouse interactions.
  // vif            : <object> vif got from AX/visualization
  // renderOptions  : <object> got from this overlays prepare method
  // overlayOptions : object
  // overlayOptions.renderLayersBefore : <string> renders all mapbox-gl-layers for this overlay
  //                                      before given layer-id
  render(vif, renderOptions, overlayOptions) {
    this._shapes.render(vif, renderOptions, overlayOptions);

    this._mouseInteractionHandler.register(
      this._shapes.getLayerId(SHAPE_LAYERS.SHAPES_FILL),
      vif,
      renderOptions, {
        highlight: true,
        popupOnMouseOver: !_.isEmpty(vif.getAllFlyoutColumns())
      }
    );

    return renderOptions;
  }

  // Makes required soql calls
  //    * getting top values for coloring by.
  // and returns the renderOptions.
  async prepare(vif, metaDataPromise) {
    const colorByColumn = vif.getShapeColorByColumn();

    const [colorByBuckets, datasetMetadata] = await Promise.all([
      RenderByHelper.getColorByBuckets(vif, colorByColumn, null, metaDataPromise, vif.isCustomColorPalette()),
      metaDataPromise
    ]);

    return {
      colorBy: COLOR_BY_CATEGORY_ALIAS,
      colorByBuckets,
      datasetMetadata,
      dataUrl: this._getDataUrl(vif, colorByBuckets),
      idBy: ROWID_ALIAS,
      layerStyles: getBasemapLayerStyles(vif),
      legendItems: colorByBuckets
    };
  }

  destroy() {
    this._shapes.destroy();
    this._mouseInteractionHandler.unregister(
      this._shapes.getLayerId(SHAPE_LAYERS.SHAPES_FILL)
    );
  }

  _getDataUrl(vif, colorByBuckets) {
    const columnName = vif.getColumnName();
    const colorByColumn = vif.getShapeColorByColumn();
    const escapedColumnName = SoqlHelpers.escapeColumnName(columnName);

    let conditions = [`{{'${columnName}' column condition}}`];
    const filters = SoqlHelpers.whereClauseFilteringOwnColumn(vif, 0);
    if (!_.isEmpty(filters)) {
      conditions.push(filters);
    }

    let selects = [`simplify_preserve_topology(snap_to_grid(${escapedColumnName},{snap_precision}),{simplify_precision}),` +
      `min(:id) as ${ROWID_ALIAS}`];
    let groups = [`simplify_preserve_topology(snap_to_grid(${escapedColumnName},{snap_precision}),{simplify_precision})`];

    if (_.isString(colorByColumn) && !_.isEmpty(colorByBuckets)) {
      // We are not grouping by colorByColumn. In case that column had 10K unique values,
      // then grouping by the colorByColumn and snapToGrid, will return
      // 10K * snappedToGrid location => number of results. Which will be too much.
      // Instead, we are only interesed in the top x values(colorByCategories) in the colorbyColumn.
      // So we select/group the remaining values as OTHER_COLOR_BY_CATEGORY and the top x in separate groups.

      // We are concatenating empty string to the resizeBy column to convert it to string.
      // Otherwise, depending on whether it is a numeric column or string column, we need to
      // use quotes around values(colorByCategories value) in case statement.
      selects.push(`max(${SoqlHelpers.escapeColumnName(colorByColumn)}||'') as ${COLOR_BY_CATEGORY_ALIAS}`);
    }
    selects.push(`count(*) as ${COUNT_ALIAS}`);

    const query = `select ${selects.join(',')} ` +
      `where ${conditions.join(' AND ')} ` +
      `group by ${groups.join(',')} ` +
      'limit 200000 ';

    return getTileUrl(vif.getDomain(), vif.getDatasetUid(), query, vif.getSimplificationLevel());
  }
}
